import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useEffect, useState } from 'react';
import { z } from 'zod';
import { DonePage } from '../../components/onboarding/DonePage.js';
import {
  connectedAccounts,
  NetworksStep,
  type OAuthReturn,
  ONBOARDING_PROVIDERS,
  useOnboardingStatuses,
} from '../../components/onboarding/NetworksStep.js';
import { OnboardingShell } from '../../components/onboarding/OnboardingShell.js';
import { useIntegrationProducts } from '../../lib/adNetwork.hooks.js';
import { useAuthenticatedSession } from '../../lib/useAuthenticatedSession.js';

const onboardingSearchSchema = z.object({
  connected: z.literal('1').optional().catch(undefined),
  error: z.string().optional().catch(undefined),
  provider: z.enum(ONBOARDING_PROVIDERS).optional().catch(undefined),
  productId: z.coerce.number().int().positive().optional().catch(undefined),
});

function OnboardingPage(): ReactNode {
  const { session, onSignOut } = useAuthenticatedSession();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const products = useIntegrationProducts();
  // The last step: once Done is clicked (or a network is already connected) there is no way back.
  const [doneClicked, setDoneClicked] = useState(false);
  // Back from a network's consent screen: keep where it returned to, then clear the URL.
  const [oauthReturn] = useState<OAuthReturn | null>(() =>
    search.provider === undefined
      ? null
      : {
          provider: search.provider,
          connected: search.connected === '1',
          error: search.error ?? null,
        },
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: clear the OAuth params once, on mount
  useEffect(() => {
    if (search.provider !== undefined)
      void navigate({ to: '/onboarding', search: {}, replace: true });
  }, []);

  // ponytail: a new self-serve client has exactly one product; a picker if that changes.
  const product = products.data?.find((p) => p.id === search.productId) ?? products.data?.[0];
  const statuses = useOnboardingStatuses(product?.id);
  const statusesLoaded = ONBOARDING_PROVIDERS.every((p) => !statuses[p].isPending);
  const shownView =
    (doneClicked ? 'done' : null) ??
    (oauthReturn !== null
      ? 'networks'
      : !statusesLoaded
        ? null
        : ONBOARDING_PROVIDERS.some((p) => connectedAccounts(statuses, p) > 0)
          ? 'done'
          : 'networks');

  const email = session?.user.email ?? null;
  if (products.isPending || (product !== undefined && shownView === null)) {
    return (
      <OnboardingShell
        email={email}
        onSignOut={() => void onSignOut()}
        step="networks"
        title="Getting things ready"
        note=""
      >
        <CircularProgress size={28} />
      </OnboardingShell>
    );
  }
  if (product === undefined) {
    // No way out to the dashboard here: until it has an ad account the gate would bring it back.
    return (
      <OnboardingShell
        email={email}
        onSignOut={() => void onSignOut()}
        step="networks"
        title="Connecting isn't open for this workspace yet"
        note="Write to csm@alison.ai and we'll set it up with you."
      >
        <Typography data-testid="onboarding-unavailable" color="text.secondary">
          Integrations aren't available for your current brand scope.
        </Typography>
      </OnboardingShell>
    );
  }
  if (shownView === 'done') {
    return <DonePage email={email} onSignOut={() => void onSignOut()} />;
  }
  return (
    <NetworksStep
      email={email}
      onSignOut={() => void onSignOut()}
      productId={product.id}
      oauthReturn={oauthReturn}
      onDone={() => setDoneClicked(true)}
    />
  );
}

export const Route = createFileRoute('/onboarding/')({
  validateSearch: (search) => onboardingSearchSchema.parse(search),
  component: OnboardingPage,
});
